import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CorporateNav from '../components/CorporateNav';
import CorporateHero from '../components/CorporateHero';
import CorporateEcosystem from '../components/CorporateEcosystem';
import RegionSelector from '../components/RegionSelector';
import DigitalPlatforms from '../components/DigitalPlatforms';
import CategorySection from '../components/CategorySection';
import IndustriesServed from '../components/IndustriesServed';
import InvestorRelations from '../components/InvestorRelations';
import PaymentCenterCTA from '../components/PaymentCenterCTA';
import CorporateTrust from '../components/CorporateTrust';
import MediaSection from '../components/MediaSection';
import CorporateCTA from '../components/CorporateCTA';
import CorporateFooter from '../components/CorporateFooter';

// Region-specific content (preserved from previous version)
const regionContent = {
  'united-states': {
    name: 'United States',
    categories: {
      'digital-infrastructure': {
        title: 'Digital Infrastructure & Online Presence',
        subtitle: 'Enterprise Technology Solutions',
        items: [
          { label: 'Websites & E-Commerce', link: 'https://web.sebasalliance.com/' },
          { label: 'Hosting', link: 'https://web.sebasalliance.com/' },
          'Marketing',
          'Cloud Infrastructure'
        ],
        link: 'https://web.sebasalliance.com/',
        external: true,
        dropdown: ['SAG Cloud Services', 'Enterprise Web Development', 'Digital Marketing Suite', 'API Infrastructure']
      },
      'financial-technology': {
        title: 'Financial Technology & Bank Integrations',
        subtitle: 'Payment & Banking Solutions',
        items: [
          { label: 'Secure Payment Gateways', link: 'https://caribbeanpaymentsalliance.com' },
          'ACH',
          'Scotiabank & Global APIs',
          'Merchant Services'
        ],
        link: 'https://caribbeanpaymentsalliance.com',
        external: true,
        dropdown: ['US Payment Infrastructure', 'Merchant Account Services', 'Banking API Integration', 'Compliance Solutions']
      },
      'capital-financing': {
        title: 'Business Capital & Financing',
        subtitle: 'Business Growth Solutions',
        items: [
          { label: 'SBA Loans', link: '/sba-financing' },
          'Credit Access',
          { label: 'Investor Relations', link: '/investor' },
          'Growth Financing'
        ],
        link: '/sba-financing',
        dropdown: [
          { label: 'SBA Loan Programs', link: '/sba-financing' },
          'US Business Capital',
          'Growth Financing',
          { label: 'Investment Partnerships', link: '/investor' }
        ]
      },
      'advisory-services': {
        title: 'Advisory & Professional Services',
        subtitle: 'Strategic Business Consulting',
        items: ['Accounting', 'Real Estate', 'Political Solutions', 'Corporate Strategy'],
        link: null,
        dropdown: ['Corporate Strategy', 'Financial Advisory', 'M&A Consulting', 'Regulatory Compliance']
      }
    }
  },
  'caribbean': {
    name: 'Caribbean',
    categories: {
      'digital-infrastructure': {
        title: 'Digital Infrastructure & Online Presence',
        subtitle: 'SAG Technology Solutions',
        items: [
          { label: 'Websites & E-Commerce', link: 'https://web.sebasalliance.com/' },
          { label: 'Hosting', link: 'https://web.sebasalliance.com/' },
          'Marketing',
          'Island Connectivity'
        ],
        link: 'https://web.sebasalliance.com/',
        external: true,
        dropdown: ['SAG Cloud Caribbean', 'Island Web Solutions', 'Regional E-Commerce', 'Connectivity Services']
      },
      'financial-technology': {
        title: 'Financial Technology & Bank Integrations',
        subtitle: 'Caribbean Payments Alliance',
        items: [
          { label: 'Secure Payment Gateways', link: 'https://caribbeanpaymentsalliance.com' },
          'ACH',
          'Scotiabank & Global APIs',
          'Regional Bank Partnerships'
        ],
        link: 'https://caribbeanpaymentsalliance.com',
        external: true,
        dropdown: [
          { label: 'Caribbean Payments Alliance', link: 'https://caribbeanpaymentsalliance.com' },
          'Regional Gateway Solutions',
          'Island Banking Integration',
          'Multi-Currency Processing'
        ]
      },
      'capital-financing': {
        title: 'Business Capital & Financing',
        subtitle: 'Real Estate & Business Financing',
        items: [
          { label: 'SBA Loans', link: '/sba-financing' },
          'Credit Access',
          { label: 'Investor Relations', link: '/investor' },
          { label: 'Real Estate Financing', link: 'https://apcestateflow.com/financing' }
        ],
        link: '/sba-financing',
        dropdown: [
          { label: 'APC EstateFlow Financing', link: 'https://apcestateflow.com/financing' },
          { label: 'Caribbean Business Loans', link: '/sba-financing' },
          'Development Funding',
          { label: 'Regional Investment', link: '/investor' }
        ]
      },
      'advisory-services': {
        title: 'Advisory & Professional Services',
        subtitle: 'Caribbean Business Advisory',
        items: [
          'Accounting',
          'Real Estate',
          { label: 'Secure Communications', link: '/secure-communications' },
          'Government Relations'
        ],
        link: null,
        dropdown: [
          'Caribbean Real Estate Advisory',
          'Financial Planning',
          { label: 'Secure Communications', link: '/secure-communications' },
          'Public Sector Consulting'
        ]
      }
    }
  },
  'global': {
    name: 'Global',
    categories: {
      'digital-infrastructure': {
        title: 'Digital Infrastructure & Online Presence',
        subtitle: 'Worldwide Technology Platform',
        items: [
          { label: 'Websites & E-Commerce', link: 'https://web.sebasalliance.com/' },
          { label: 'Hosting', link: 'https://web.sebasalliance.com/' },
          'Marketing',
          'Global Cloud Network'
        ],
        link: 'https://web.sebasalliance.com/',
        external: true,
        dropdown: ['Global Cloud Infrastructure', 'International Web Services', 'Cross-Border E-Commerce', 'Worldwide Marketing']
      },
      'financial-technology': {
        title: 'Financial Technology & Bank Integrations',
        subtitle: 'International Payment Systems',
        items: [
          { label: 'Secure Payment Gateways', link: 'https://caribbeanpaymentsalliance.com' },
          'Multi-Currency Processing',
          'SWIFT Integration',
          'Cross-Border Transactions'
        ],
        link: 'https://caribbeanpaymentsalliance.com',
        external: true,
        dropdown: ['Global Payment Network', 'Currency Exchange Services', 'International Bank Partnerships', 'Trade Finance Solutions']
      },
      'capital-financing': {
        title: 'Business Capital & Financing',
        subtitle: 'International Investment',
        items: [
          'Global Investment',
          'International Lending',
          'Cross-Border Capital',
          { label: 'Sovereign Partnerships', link: '/investor' }
        ],
        link: '/investor',
        dropdown: [
          { label: 'International Investment Fund', link: '/investor' },
          'Global Business Capital',
          'Sovereign Wealth Advisory',
          'Cross-Border M&A'
        ]
      },
      'advisory-services': {
        title: 'Advisory & Professional Services',
        subtitle: 'Global Strategic Advisory',
        items: [
          'International Strategy',
          'Global Market Entry',
          'Regulatory Navigation',
          'Diplomatic Relations'
        ],
        link: null,
        dropdown: ['Global Strategy Consulting', 'Market Entry Advisory', 'International Compliance', 'Government Affairs']
      }
    }
  }
};

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
  >
    {children}
  </motion.div>
);

const LandingPage = () => {
  const [selectedRegion, setSelectedRegion] = useState('caribbean');
  const currentContent = regionContent[selectedRegion];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Sticky corporate navigation */}
      <CorporateNav />

      {/* Premium corporate hero */}
      <CorporateHero />

      {/* Ecosystem diagram with full descriptions + summary panel */}
      <Reveal>
        <CorporateEcosystem />
      </Reveal>

      {/* Region selector + dynamic category content (preserved) */}
      <div id="solutions">
        <RegionSelector
          selectedRegion={selectedRegion}
          onRegionChange={setSelectedRegion}
          regionName={currentContent.name}
        />
        <Reveal>
          <CategorySection categories={currentContent.categories} region={selectedRegion} />
        </Reveal>
      </div>

      {/* Industries */}
      <Reveal>
        <IndustriesServed />
      </Reveal>

      {/* Existing digital platforms grid */}
      <Reveal>
        <DigitalPlatforms />
      </Reveal>

      {/* Investor Relations */}
      <Reveal>
        <InvestorRelations />
      </Reveal>

      {/* Payment Center CTA */}
      <Reveal>
        <PaymentCenterCTA />
      </Reveal>

      {/* Corporate Trust Strip (replaces old PartnersSection) */}
      <Reveal>
        <CorporateTrust />
      </Reveal>

      {/* Media Section */}
      <Reveal>
        <MediaSection />
      </Reveal>

      {/* Final Corporate CTA */}
      <CorporateCTA />

      {/* Corporate Footer */}
      <CorporateFooter />
    </div>
  );
};

export default LandingPage;
